"use client";

import React, { useState, useEffect, Suspense } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { MatchingQuiz, Button, Input } from "@repo/ui";
import type { QuizData } from "@repo/supabase/services";
import { clientLogger } from "@repo/observability/client";
import { useQuiz } from "../../contexts/AppContext";
import { useUser } from "../../contexts/UserContext";
import { trackQuizStart, trackQuizCompletion } from "../../components/GoogleTagManager";
import { getReferralData } from "../../lib/referral-tracking";
import { getPromoCode } from "../../lib/promo-storage";
import { AlertCircle } from "lucide-react";

interface QuizAnswers {
  ageVerification: 'yes' | 'no';
  sessionType: 'individual' | 'couples';
  needs: string[];
  insurance: string[];
  therapistType: 'resident' | 'associate';
  rate: number;
  timezone: string;
  availability: string[];
  province?: string;
}

function QuizPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { quizAnswers: contextQuizAnswers, setQuizAnswers: setContextQuizAnswers } = useQuiz();
  const { profile: userProfile } = useUser();
  const [currentStep, setCurrentStep] = useState<'quiz' | 'email'>('quiz');
  const [pendingAnswers, setPendingAnswers] = useState<QuizAnswers | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const urlPromoCode = searchParams.get('promo');
  const [promoCode, setPromoCode] = useState<string | null>(urlPromoCode);

  useEffect(() => {
    if (!urlPromoCode) {
      const storedPromo = getPromoCode();
      if (storedPromo) setPromoCode(storedPromo);
    }
  }, [urlPromoCode]);

  const handleQuizComplete = async (answers: QuizAnswers) => {
    setContextQuizAnswers(answers as never);
    setPendingAnswers(answers);

    if (userProfile) {
      await submitQuizDataWithUser(userProfile.id as string, answers);
    } else {
      // Guest flow — collect email + firstName inline, then submit as guest quiz.
      setCurrentStep('email');
    }
  };

  const submitGuestQuiz = async () => {
    if (!pendingAnswers) {
      setSubmissionError('Lost your quiz answers. Please retake the quiz.');
      return;
    }

    // Validate
    let valid = true;
    if (!firstName.trim()) {
      setNameError('Please enter your first name');
      valid = false;
    } else {
      setNameError(null);
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Please enter a valid email address');
      valid = false;
    } else {
      setEmailError(null);
    }
    if (!valid) return;

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const referralData = getReferralData();
      const quizData: QuizData & { email: string; firstName: string } = {
        ageVerification: pendingAnswers.ageVerification,
        sessionType: pendingAnswers.sessionType,
        needs: pendingAnswers.needs,
        insurance: pendingAnswers.insurance,
        therapistType: pendingAnswers.therapistType,
        rate: pendingAnswers.rate,
        timezone: pendingAnswers.timezone,
        availability: pendingAnswers.availability,
        province: pendingAnswers.province,
        promoCode: promoCode || undefined,
        isPaidReferrer: referralData.isPaidReferrer,
        referralSource: referralData.referralSource,
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
      };

      const response = await fetch('/api/quiz/temp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quizData),
      });
      const result = await response.json();

      if (!response.ok || !result.success || !result.quizId) {
        setSubmissionError(result.error || 'Failed to save your quiz. Please try again.');
        setIsSubmitting(false);
        return;
      }

      try {
        await fetch('/api/quiz/track-completion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quizId: result.quizId,
            sessionType: pendingAnswers.sessionType,
            therapistType: pendingAnswers.therapistType,
            rate: pendingAnswers.rate,
            timezone: pendingAnswers.timezone,
          }),
        });
      } catch (trackError) {
        console.error('[Quiz] Error tracking guest completion:', trackError);
      }

      trackQuizStart(result.quizId, pendingAnswers.sessionType, pendingAnswers.therapistType, pendingAnswers.rate);
      trackQuizCompletion(result.quizId, pendingAnswers.sessionType, pendingAnswers.therapistType, pendingAnswers.rate);

      router.push(`/matched?id=${result.quizId}`);
    } catch (err) {
      console.error('[QuizPage] Error submitting guest quiz:', err);
      void clientLogger.error(err, {
        componentName: 'QuizPage',
        action: 'submitGuestQuiz',
        feature: 'onboarding',
        userImpact: 'degraded',
      });
      setSubmissionError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const submitQuizDataWithUser = async (userId: string, answers: QuizAnswers) => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const referralData = getReferralData();
      const quizData: QuizData = {
        ageVerification: answers.ageVerification,
        sessionType: answers.sessionType,
        needs: answers.needs,
        insurance: answers.insurance,
        therapistType: answers.therapistType,
        rate: answers.rate,
        timezone: answers.timezone,
        availability: answers.availability,
        province: answers.province,
        promoCode: promoCode || undefined,
        isPaidReferrer: referralData.isPaidReferrer,
        referralSource: referralData.referralSource,
      };

      const response = await fetch('/api/quiz/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quizData),
      });
      const result = await response.json();
      const { success, quizId, error } = result;

      if (success && quizId) {
        trackQuizStart(quizId, answers.sessionType, answers.therapistType, answers.rate);
      }

      if (!success || error) {
        setSubmissionError(error || 'Failed to save your preferences. Please try again.');
        return;
      }

      setContextQuizAnswers({ ...answers, matchingQuizId: quizId } as never);

      // Server-side conversion tracking (zero-PII).
      try {
        await fetch('/api/quiz/track-completion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quizId,
            sessionType: answers.sessionType,
            therapistType: answers.therapistType,
            rate: answers.rate,
            timezone: answers.timezone,
          }),
        });
      } catch (trackError) {
        console.error('[Quiz] Error tracking completion:', trackError);
      }

      trackQuizCompletion(quizId || '', answers.sessionType, answers.therapistType, answers.rate);

      if (quizId) {
        router.push(`/matched?id=${quizId}`);
      } else {
        setSubmissionError('Failed to generate matches. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      void clientLogger.error(error, {
        componentName: 'QuizPage',
        action: 'handleSubmit',
        feature: 'onboarding',
        userImpact: 'degraded',
      });
      setSubmissionError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (currentStep === 'email' && pendingAnswers) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                One last step to see your matches
              </h2>
              <p className="text-gray-600">
                Where should we send your therapist recommendations?
              </p>
            </div>

            {submissionError && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-6">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{submissionError}</span>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitGuestQuiz();
              }}
              className="space-y-5"
            >
              <Input
                type="text"
                name="firstName"
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                error={nameError || undefined}
                required
                autoComplete="given-name"
              />
              <Input
                type="email"
                name="email"
                label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                error={emailError || undefined}
                required
                autoComplete="email"
              />
              <Button type="submit" className="w-full" isLoading={isSubmitting}>
                {isSubmitting ? 'Finding your matches...' : 'See my matches'}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                No account required. We'll only email you about your matches.
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MatchingQuiz onComplete={handleQuizComplete} isSubmitting={isSubmitting} showProvince />
      {submissionError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg max-w-md">
          <p className="text-sm">{submissionError}</p>
        </div>
      )}
    </div>
  );
}

export default function QuizPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading quiz...</p>
        </div>
      </div>
    }>
      <QuizPageContent />
    </Suspense>
  );
}
